import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ExternalLink, Copy, Check } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "../../components/base/alert";
import { Button } from "../../components/base/button";
import { useInAppBrowser } from "../../hooks/useInAppBrowser";
import { useUiTranslation } from "../../i18n";
import { cn } from "../../lib/utils";
/**
 * Warning component displayed when user is in an in-app browser.
 * Google OAuth does not work in embedded browsers due to security restrictions.
 */
export function InAppBrowserWarning({ targetUrl, forceShow = false, className, autoOpen = true, }) {
    const { t } = useUiTranslation();
    const { isInAppBrowser, browserName, canOpenExternalBrowser, tryOpenExternal, } = useInAppBrowser();
    const hasAutoOpened = useRef(false);
    const [copied, setCopied] = useState(false);
    // Auto-open external browser for LINE
    useEffect(() => {
        if (autoOpen &&
            isInAppBrowser &&
            browserName === "line" &&
            canOpenExternalBrowser &&
            !hasAutoOpened.current) {
            hasAutoOpened.current = true;
            tryOpenExternal(targetUrl);
        }
    }, [
        autoOpen,
        isInAppBrowser,
        browserName,
        canOpenExternalBrowser,
        tryOpenExternal,
        targetUrl,
    ]);
    if (!isInAppBrowser && !forceShow)
        return null;
    const handleOpenExternal = () => {
        const success = tryOpenExternal(targetUrl);
        if (!success) {
            // Copy URL to clipboard as fallback
            const url = targetUrl ?? window.location.href;
            navigator.clipboard?.writeText(url);
        }
    };
    const handleCopyUrl = async () => {
        const url = targetUrl ?? window.location.href;
        let success = false;
        // Try modern clipboard API first
        if (navigator.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(url);
                success = true;
            }
            catch {
                // Clipboard API failed, try fallback
            }
        }
        // Fallback: use textarea + execCommand
        if (!success) {
            const textarea = document.createElement("textarea");
            textarea.value = url;
            textarea.style.position = "fixed";
            textarea.style.left = "-9999px";
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand("copy");
                success = true;
            }
            catch {
                // execCommand also failed
            }
            document.body.removeChild(textarea);
        }
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };
    const browserDisplayName = browserName
        ? t(`inAppBrowser.browsers.${browserName}`, { defaultValue: browserName })
        : t("inAppBrowser.browsers.unknown");
    return (_jsxs(Alert, { variant: "destructive", className: cn("mb-4 border-amber-500 bg-amber-50 text-amber-900 dark:border-amber-500 dark:bg-amber-950 dark:text-amber-100 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400", className), children: [_jsx(AlertTriangle, { className: "h-4 w-4" }), _jsx(AlertTitle, { className: "text-amber-900 dark:text-amber-100", children: t("inAppBrowser.warning.title") }), _jsxs(AlertDescription, { className: "space-y-3 text-amber-800 dark:text-amber-200", children: [_jsx("p", { children: t("inAppBrowser.warning.description", {
                            browser: browserDisplayName,
                        }) }), canOpenExternalBrowser ? (_jsxs(Button, { variant: "outline", size: "sm", onClick: handleOpenExternal, className: "border-amber-600 text-amber-900 hover:bg-amber-100 dark:border-amber-400 dark:text-amber-100 dark:hover:bg-amber-900", children: [_jsx(ExternalLink, { className: "h-4 w-4 mr-2" }), t("inAppBrowser.warning.openExternal")] })) : (_jsxs("div", { className: "text-sm space-y-2", children: [_jsx("p", { children: t("inAppBrowser.warning.manualInstructions") }), _jsxs("ol", { className: "list-decimal list-inside space-y-1", children: [_jsx("li", { children: t("inAppBrowser.warning.step1") }), _jsx("li", { children: t("inAppBrowser.warning.step2") }), _jsx("li", { children: t("inAppBrowser.warning.step3") })] }), _jsx(Button, { variant: "outline", size: "sm", onClick: handleCopyUrl, className: "border-amber-600 text-amber-900 hover:bg-amber-100 dark:border-amber-400 dark:text-amber-100 dark:hover:bg-amber-900", children: copied ? (_jsxs(_Fragment, { children: [_jsx(Check, { className: "h-4 w-4 mr-2" }), t("inAppBrowser.warning.copied")] })) : (_jsxs(_Fragment, { children: [_jsx(Copy, { className: "h-4 w-4 mr-2" }), t("inAppBrowser.warning.copyUrl")] })) })] }))] })] }));
}
//# sourceMappingURL=InAppBrowserWarning.js.map